import { scaleAssignmentSchema } from "@fatbees/schemas";
import { toast } from "@fatbees/ui-components";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { deleteAssignment, putAssignment } from "../api";

const REFETCH_INTERVAL = 1000 * 60 * 15; // 15 minutes
const QUERY_KEY = ["hive-assignments"];
const HIVES_QUERY_KEY = ["hives"];
const OBSERVATIONS_QUERY_KEY = ["observations"];
const READINGS_QUERY_KEY = ["readings"];

export function useHiveAssignmentsCollectionInstance(queryClient: QueryClient) {
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [queryClient]);

  return useMemo(() => {
    function invalidateDependentQueries() {
      queryClient.invalidateQueries({ queryKey: HIVES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OBSERVATIONS_QUERY_KEY });
      queryClient.removeQueries({ queryKey: READINGS_QUERY_KEY });
    }

    return createCollection(
      queryCollectionOptions({
        id: "hive-assignments",
        queryKey: QUERY_KEY,
        queryFn: async () => {
          const response = await fetch("/api/hive-assignments");
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = await response.json();

          return body.map((assignment: unknown) =>
            scaleAssignmentSchema.parse(assignment),
          );
        },
        queryClient,
        getKey: (item) => item.id,
        schema: scaleAssignmentSchema,
        onInsert: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putAssignment(modified);

                collection.utils.writeInsert([modified]);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save hive assignment", type: "error" });
            throw error;
          }

          invalidateDependentQueries();
          return { refetch: false };
        },
        onUpdate: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putAssignment(modified);

                collection.utils.writeUpdate(modified);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save hive assignment", type: "error" });
            throw error;
          }

          invalidateDependentQueries();
          return { refetch: false };
        },
        onDelete: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await deleteAssignment(modified);

                collection.utils.writeDelete(modified.id);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save hive assignment", type: "error" });
            throw error;
          }

          invalidateDependentQueries();
          return { refetch: false };
        },
      }),
    );
  }, [queryClient]);
}

export type HiveAssignmentsCollection = ReturnType<
  typeof useHiveAssignmentsCollectionInstance
>;
